/*
  KLUDI collection pagination: progressive enhancement for the mobile "Load
  more" button only. The desktop numbered boxes and "Next" link stay inside
  focal's own <page-pagination ajax> element and keep its existing
  replace-the-page behaviour (theme.js ProductFacet._rerender) -- each one
  is a plain link that swaps #facet-main the way clicking a page number
  already does there, on purpose, so the two controls stay consistent with
  each other. "Load more" is the one control whose name promises an
  accumulating list, so only it fetches the next page and moves its cards
  into the existing grid instead of navigating; without javascript it is
  still a plain link to the next page, rendered server-side by
  snippets/pagination.liquid.

  lives in its own <kludi-pagination> element rather than focal's
  <page-pagination>, so its click never reaches that element's own
  data-page click delegate (which would otherwise trigger a full facet
  replace instead of an append)

  wrapped in an iife with a customElements.get guard because the section
  that renders this script can appear more than once during theme-editor
  previews and this file must survive being evaluated more than once per
  page
*/
(function () {
  if (customElements.get('kludi-pagination')) return;

  class KludiPagination extends HTMLElement {
    connectedCallback() {
      this.grid = document.querySelector(this.dataset.gridSelector || '#product-grid');
      this.isLoading = false;

      // without a matching grid on the page there is nowhere safe to append
      // fetched cards into, so leave the control as a plain page link
      if (!this.grid) return;

      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick(event) {
      const trigger = event.target.closest('[data-kludi-pagination-load-more]');
      if (!trigger || this.isLoading) return;

      event.preventDefault();
      this.loadPage(trigger.href);
    }

    async loadPage(url) {
      this.isLoading = true;
      this.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('kludi-pagination: fetch failed');

        const html = await response.text();
        const nextDocument = new DOMParser().parseFromString(html, 'text/html');
        const gridSelector = this.dataset.gridSelector || '#product-grid';
        const nextGrid = nextDocument.querySelector(gridSelector);
        const nextPagination = nextDocument.querySelector('.kludi-pagination');

        if (!nextGrid) throw new Error('kludi-pagination: next page has no matching grid');

        // move rather than clone the fetched cards: a custom element upgrades
        // on connection to the document regardless of how it got there, so
        // each moved <kludi-card> runs its own connectedCallback -- including
        // the resize observer it sets up for the finish-strip "+N" count --
        // the same way the cards already on the page did on first load
        while (nextGrid.firstChild) {
          this.grid.appendChild(nextGrid.firstChild);
        }

        if (nextPagination) {
          const container = this.closest('.kludi-pagination');
          if (container) container.innerHTML = nextPagination.innerHTML;
        }

        history.replaceState(null, '', url);
      } catch (error) {
        // network hiccup or an unexpected markup shape: fall back to a real
        // navigation rather than leaving the control silently inert
        window.location.href = url;
        return;
      } finally {
        this.isLoading = false;
        this.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('kludi-pagination', KludiPagination);
})();
